/* eslint-disable no-console */
// Console usage in this file is intentional - logs run in injected print window script
// where the logger module is not available

// Development check
const __DEV__ = import.meta.env.MODE === 'development';

export interface PrintOptions {
  title: string;           // Tên file PDF
  htmlBody: string;         // Nội dung HTML body
  headExtra?: string;       // CSS/link tags bổ sung
  pageSize?: 'A4' | 'A3';   // Kích thước trang
  pageMargin?: string;      // Margin (vd: '16mm')
  orientation?: 'portrait' | 'landscape';
  onFailIframe?: () => void; // Fallback nếu window.open fail
}

export async function printInNewWindow(options: PrintOptions): Promise<void> {
  const {
    title,
    htmlBody,
    headExtra = '',
    pageSize = 'A4',
    pageMargin = '16mm',
    orientation = 'portrait',
    onFailIframe
  } = options;

  // Mở window ngay trong user gesture (synchronously)
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    __DEV__ && console.warn('[printInNewWindow] Popup blocked, calling fallback');
    onFailIframe?.();
    return;
  }

  // Viết skeleton HTML ngay để giữ window
  printWindow.document.write(`
    <!doctype html>
    <html>
    <head><meta charset="utf-8"><title>${title}</title></head>
    <body><div style="padding:20px;font-family:system-ui">Đang chuẩn bị báo cáo...</div></body>
    </html>
  `);

  // Build full HTML với đầy đủ CSS + polishes
  const fullHtml = `
<!doctype html>
<html lang="vi">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <link rel="icon" href="https://storage.googleapis.com/gpt-engineer-file-uploads/5Ahiblr9aqebkhCMcYkI21PicVv2/social-images/social-1758241299872-solid logo.png" type="image/png">
  
  ${headExtra}
  
  <style>
    /* CSS Variables từ design system */
    :root {
      --background: 0 0% 100%;
      --foreground: 222.2 84% 4.9%;
      --card: 0 0% 100%;
      --card-foreground: 222.2 84% 4.9%;
      --popover: 0 0% 100%;
      --popover-foreground: 222.2 84% 4.9%;
      --primary: 222.2 47.4% 11.2%;
      --primary-foreground: 210 40% 98%;
      --secondary: 210 40% 96.1%;
      --secondary-foreground: 222.2 47.4% 11.2%;
      --muted: 210 40% 96.1%;
      --muted-foreground: 215.4 16.3% 46.9%;
      --accent: 210 40% 96.1%;
      --accent-foreground: 222.2 47.4% 11.2%;
      --destructive: 0 84.2% 60.2%;
      --destructive-foreground: 210 40% 98%;
      --border: 214.3 31.8% 91.4%;
      --input: 214.3 31.8% 91.4%;
      --ring: 222.2 84% 4.9%;
      --radius: 0.5rem;
      --chart-1: 221 83% 53%;
      --chart-2: 142 76% 36%;
      --chart-3: 48 96% 53%;
      --chart-4: 25 95% 53%;
      --chart-5: 262 83% 58%;
    }
    
    /* Force color preservation */
    * { 
      -webkit-print-color-adjust: exact !important; 
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    /* Page setup */
    @page { 
      size: ${pageSize} ${orientation}; 
      margin: ${pageMargin}; 
    }
    
    /* Base styles */
    html, body {
      margin: 0;
      padding: 0;
      background: hsl(var(--background));
      color: hsl(var(--foreground));
      width: 100%;
    }
    
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
      font-size: 12px;
      line-height: 1.5;
    }
    
    /* Image overflow guards */
    img:not(.print-logo-img) {
      max-width: 100% !important;
      height: auto !important;
      display: block;
    }
    
    /* Print-specific styles */
    @media print {
      html, body { 
        height: auto; 
        overflow: visible !important;
        margin: 0;
        padding: 0;
      }
      
      /* Remove scrollbars */
      .overflow-auto,
      .overflow-x-auto,
      .overflow-y-auto,
      .overflow-hidden,
      .overflow-scroll { 
        overflow: visible !important; 
      }
      
      /* Fix sticky elements */
      .sticky, 
      [class*="sticky"] { 
        position: static !important; 
        top: auto !important; 
      }
      
      /* Enhanced page break handling (WebKit-compatible) */
      .avoid-break,
      .photo-keyplan-pair,
      .specs-table tr,
      .timeline-item {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        -webkit-column-break-inside: avoid !important;
        -webkit-region-break-inside: avoid !important;
      }
      
      /* Table header repeat on multi-page tables */
      thead {
        display: table-header-group !important;
      }
      
      tfoot {
        display: table-footer-group !important;
      }
      
      /* Layer header print styles */
      .layer-header {
        border: 2px solid #9ca3af !important;
        background: linear-gradient(135deg, #f3f4f6, #ffffff, #f3f4f6) !important;
        color: #111827 !important;
        box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
        page-break-inside: avoid !important;
        page-break-after: avoid !important;
        padding: 16px 24px;
        border-radius: 8px;
        margin-bottom: 24px;
        position: relative;
      }
      
      .layer-header::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 6px;
        background: linear-gradient(to bottom, #374151, #6b7280) !important;
        border-radius: 6px 0 0 6px;
      }
    }
    
    /* iOS Safari specific fixes */
    @supports (-webkit-touch-callout: none) {
      body { 
        -webkit-text-size-adjust: 100%; 
        -webkit-font-smoothing: antialiased;
      }
    }
    
    /* Hide preview-only elements */
    .no-print, 
    [data-no-print] { 
      display: none !important; 
    }
    
    @media print {
      /* ✅ FIX: Print layout width and pin transforms */
      .print-layout {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      .pin-marker {
        transform: translate(-50%, -50%) !important;
      }
    }
  </style>
</head>
<body>
  ${htmlBody}
  
  <script>
    (function() {
      const __DEV__ = ${__DEV__};
      const startTime = performance.now();
      __DEV__ && console.log('[Print Window] Initializing at', new Date().toISOString());
      
      // Wait for all images to load
      function waitForImages() {
        const images = Array.from(document.images);
        __DEV__ && console.log(\`[Print Window] Found \${images.length} images\`);
        
        if (images.length === 0) {
          return Promise.resolve();
        }
        
        return Promise.all(
          images.map((img, idx) => {
            if (img.complete && img.naturalHeight !== 0) {
              __DEV__ && console.log(\`[Print Window] Image \${idx+1} already loaded\`);
              return Promise.resolve();
            }
            
            return new Promise(resolve => {
              const timeout = setTimeout(() => {
                __DEV__ && console.warn(\`[Print Window] Image \${idx+1} timeout: \${img.src}\`);
                resolve();
              }, 5000); // 5s timeout per image
              
              img.addEventListener('load', () => {
                clearTimeout(timeout);
                __DEV__ && console.log(\`[Print Window] Image \${idx+1} loaded: \${img.src}\`);
                resolve();
              });
              
              img.addEventListener('error', () => {
                clearTimeout(timeout);
                __DEV__ && console.error(\`[Print Window] Image \${idx+1} failed: \${img.src}\`);
                resolve(); // Don't block on errors
              });
            });
          })
        );
      }
      
      // Wait for fonts to load
      function waitForFonts() {
        if (!document.fonts || !document.fonts.ready) {
          __DEV__ && console.log('[Print Window] Fonts API not available, skipping');
          return Promise.resolve();
        }
        __DEV__ && console.log('[Print Window] Waiting for fonts...');
        return document.fonts.ready.then(() => {
          __DEV__ && console.log('[Print Window] Fonts ready');
        });
      }
      
      // Dynamically allocate heights to fill page and maximize keyplan
      function allocateHeightsPerPage() {
        const pxPerMm = 794 / 210;
        const marginMm = parseFloat('${pageMargin}'.replace('mm', ''));
        const pageHeightMm = ${orientation === 'portrait' ? (pageSize === 'A4' ? 297 : 420) : (pageSize === 'A4' ? 210 : 297)};
        const contentHeightPx = (pageHeightMm - 2 * marginMm) * pxPerMm;
        
        __DEV__ && console.log(\`[allocateHeights] Content height: \${contentHeightPx.toFixed(0)}px\`);
        
        const pages = document.querySelectorAll('.print-page');
        pages.forEach((page, idx) => {
          // FIX: page chính nó có thể là pair trong landscape mode
          const pair = page.classList.contains('photo-keyplan-pair') ? page : page.querySelector('.photo-keyplan-pair');
          if (!pair) return;
          
          // Check if landscape mode (multiple columns)
          const isLandscape = pair.querySelector('.landscape-photo-grid-with-divider, .landscape-photo-grid-single');
          
          if (isLandscape) {
            // Landscape mode: Handle multiple photos and keyplans
            const photoEls = pair.querySelectorAll('img.print-photo-img');
            // FIX: Đổi selector sang .print-mini-keyplan
            const keyplanContainers = pair.querySelectorAll('.print-mini-keyplan');
            
            if (photoEls.length === 0) return;
            
            __DEV__ && console.log(\`[allocateHeights] Page \${idx} LANDSCAPE: \${photoEls.length} photos, \${keyplanContainers.length} keyplans\`);
            
            // Calculate overhead
            const pairPadding = 8 * pxPerMm;
            const gapBetween = 6 * pxPerMm;
            const captionHeight = 40;
            const titleHeight = 30;
            const overheadPx = pairPadding + gapBetween + captionHeight + titleHeight;
            const availableForImages = contentHeightPx - overheadPx;
            
            // Allocate smaller heights for landscape (2 photos side by side)
            const photoMaxPx = Math.floor(availableForImages * 0.35);
            const keyplanMaxPx = Math.floor(availableForImages * 0.40);
            
            photoEls.forEach(photoEl => {
              photoEl.style.maxHeight = photoMaxPx + 'px';
            });
            
            keyplanContainers.forEach(container => {
              container.style.maxHeight = keyplanMaxPx + 'px';
              const svg = container.querySelector('svg');
              if (svg) svg.style.maxHeight = keyplanMaxPx + 'px';
              const img = container.querySelector('img.print-keyplan-img');
              if (img) img.style.maxHeight = keyplanMaxPx + 'px';
            });
            
            __DEV__ && console.log(\`[allocateHeights] Landscape page \${idx}: photo=\${photoMaxPx}px, keyplan=\${keyplanMaxPx}px\`);
            
          } else {
            // Portrait mode: Original single photo/keyplan logic
            const photoEl = pair.querySelector('img.print-photo-img');
            const keyplanContainer = pair.querySelector('.print-mini-keyplan');
            const keyplanEl = keyplanContainer?.querySelector('svg, img.print-keyplan-img');
            
            if (!photoEl || !keyplanEl) return;
            
            // Calculate overhead (captions, titles, gaps, padding)
            const pairPadding = 8 * pxPerMm; // 4mm top + 4mm bottom
            const gapBetween = 6 * pxPerMm;
            const captionHeight = 40; // Estimate
            const titleHeight = 30; // Estimate
            const overheadPx = pairPadding + gapBetween + captionHeight + titleHeight;
            
            const availableForImages = contentHeightPx - overheadPx;
            
            // Set base allocations
            let photoMaxPx = Math.floor(availableForImages * 0.55);
            let keyplanMaxPx = Math.floor(availableForImages * 0.35);
            
            photoEl.style.maxHeight = photoMaxPx + 'px';
            keyplanEl.style.maxHeight = keyplanMaxPx + 'px';
            if (keyplanContainer) keyplanContainer.style.maxHeight = keyplanMaxPx + 'px';
            
            // Allow reflow
            requestAnimationFrame(() => {
              const photoHeight = photoEl.getBoundingClientRect().height;
              const keyplanHeight = keyplanEl.getBoundingClientRect().height;
              const usedPx = photoHeight + keyplanHeight;
              let freePx = availableForImages - usedPx;
              
              __DEV__ && console.log(\`[allocateHeights] Page \${idx}: free=\${freePx.toFixed(0)}px, photo=\${photoHeight.toFixed(0)}px, keyplan=\${keyplanHeight.toFixed(0)}px\`);
              
              if (freePx > 8) {
                // Prioritize growing keyplan
                const keyplanMaxGrow = Math.floor(availableForImages * 0.50);
                const keyplanGrowPotential = Math.min(freePx, keyplanMaxGrow - keyplanHeight);
                
                if (keyplanGrowPotential > 0) {
                  keyplanMaxPx = keyplanHeight + keyplanGrowPotential;
                  keyplanEl.style.maxHeight = keyplanMaxPx + 'px';
                  if (keyplanContainer) keyplanContainer.style.maxHeight = keyplanMaxPx + 'px';
                  freePx -= keyplanGrowPotential;
                  
                  __DEV__ && console.log(\`[allocateHeights] Page \${idx}: grew keyplan by \${keyplanGrowPotential.toFixed(0)}px\`);
                }
                
                // If still free, grow photo
                if (freePx > 8) {
                  const photoMaxGrow = Math.floor(availableForImages * 0.60);
                  const photoGrowPotential = Math.min(freePx, photoMaxGrow - photoHeight);
                  
                  if (photoGrowPotential > 0) {
                    photoMaxPx = photoHeight + photoGrowPotential;
                    photoEl.style.maxHeight = photoMaxPx + 'px';
                    
                    __DEV__ && console.log(\`[allocateHeights] Page \${idx}: grew photo by \${photoGrowPotential.toFixed(0)}px\`);
                  }
                }
              }
            });
          }
        });
        
        __DEV__ && console.log('[allocateHeights] Done');
      }
      
      // Arrange pages to prevent overflow and force page breaks
      function arrangePages() {
        const pxPerMm = 794 / 210; // Standard A4 width ratio
        const marginMm = parseFloat('${pageMargin}'.replace('mm', ''));
        const pageHeightMm = ${orientation === 'portrait' ? (pageSize === 'A4' ? 297 : 420) : (pageSize === 'A4' ? 210 : 297)};
        const contentHeightPx = (pageHeightMm - 2 * marginMm) * pxPerMm;
        
        __DEV__ && console.log(\`[arrangePages] Page setup: \${pageHeightMm}mm height, \${marginMm}mm margins, \${contentHeightPx.toFixed(0)}px content height\`);
        
        const pages = document.querySelectorAll('.print-page');
        __DEV__ && console.log(\`[arrangePages] Found \${pages.length} print pages\`);
        
        pages.forEach((page, idx) => {
          const pageHeight = page.getBoundingClientRect().height;
          const isOverflow = pageHeight > contentHeightPx + 4; // 4px buffer
          
          if (isOverflow) {
            __DEV__ && console.warn(\`[arrangePages] Page \${idx} overflow: \${pageHeight.toFixed(0)}px > \${contentHeightPx.toFixed(0)}px\`);
            
            // If next page exists, ensure page break before it
            const nextPage = pages[idx + 1];
            if (nextPage) {
              nextPage.style.breakBefore = 'page';
              nextPage.style.pageBreakBefore = 'always';
            }
          } else {
            __DEV__ && console.log(\`[arrangePages] Page \${idx} OK: \${pageHeight.toFixed(0)}px\`);
          }
        });
        
        __DEV__ && console.log('[arrangePages] Done');
      }
      
      // ✅ FIX: Recalculate pin positions using DOMRect (same as Preview)
      function recalcPins() {
        const wrappers = document.querySelectorAll('.print-keyplan-preview');
        __DEV__ && console.log(\`[recalcPins] Found \${wrappers.length} keyplan wrappers\`);
        
        wrappers.forEach((wrapper, idx) => {
          // Use the pin stage container (marked with data-pin-stage) or fallback to wrapper
          const stage = wrapper.querySelector('[data-pin-stage]') || wrapper;
          
          // If overlay exists, pins already positioned by % inside the overlay — skip manual recalculation
          const overlay = stage.querySelector('.pin-overlay');
          if (overlay) {
            __DEV__ && console.log('[recalcPins] Wrapper ' + idx + ': pin-overlay found, skip manual reposition');
            return;
          }
          
          const img = stage.querySelector('img');
          
          if (!img || !img.complete || img.naturalWidth === 0) {
            __DEV__ && console.warn(\`[recalcPins] Container \${idx}: Image not ready\`);
            return;
          }
          
          // ✅ FIX: Use getBoundingClientRect to measure actual displayed dimensions
          const stageRect = stage.getBoundingClientRect();
          const imgRect = img.getBoundingClientRect();
          
          const offsetX = imgRect.left - stageRect.left;
          const offsetY = imgRect.top - stageRect.top;
          const displayWidth = imgRect.width;
          const displayHeight = imgRect.height;
          
          __DEV__ && console.log(\`[recalcPins] Stage \${idx}:\`, {
            stageDims: \`\${stageRect.width}x\${stageRect.height}\`,
            imageDims: \`\${img.naturalWidth}x\${img.naturalHeight}\`,
            displayDims: \`\${displayWidth.toFixed(1)}x\${displayHeight.toFixed(1)}\`,
            offset: \`(\${offsetX.toFixed(1)}, \${offsetY.toFixed(1)})\`
          });
          
          const markers = stage.querySelectorAll('.pin-marker');
          markers.forEach(marker => {
            const xPercent = parseFloat(marker.getAttribute('data-x-percent') || '0');
            const yPercent = parseFloat(marker.getAttribute('data-y-percent') || '0');
            const pinId = marker.getAttribute('data-pin-id');
            
            const pinX = offsetX + (xPercent / 100) * displayWidth;
            const pinY = offsetY + (yPercent / 100) * displayHeight;
            
            marker.style.left = \`\${pinX}px\`;
            marker.style.top = \`\${pinY}px\`;
            
            // Update corresponding label
            const label = stage.querySelector(\`.pin-label[data-pin-id="\${pinId}"]\`);
            if (label) {
              label.style.left = \`\${pinX}px\`;
              label.style.top = \`\${pinY - 18}px\`;
            }
            
            __DEV__ && console.log(\`[recalcPins] Pin \${pinId}: (\${xPercent}%, \${yPercent}%) → (\${pinX.toFixed(1)}, \${pinY.toFixed(1)})px\`);
          });
        });
        
        __DEV__ && console.log('[recalcPins] Done');
      }
      
      // Main initialization
      Promise.all([
        waitForImages(),
        waitForFonts()
      ]).then(() => {
        const loadTime = performance.now() - startTime;
        __DEV__ && console.log(\`[Print Window] All assets loaded in \${loadTime.toFixed(0)}ms\`);
        
        // ✅ Allocate heights, recalc pins, then arrange pages
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            allocateHeightsPerPage(); // Dynamically size photo/keyplan
            
            setTimeout(() => {
              recalcPins(); // Recalc after sizing
              arrangePages(); // Check page heights and enforce breaks
              
              // Small delay for final layout calculation (critical for iOS)
              setTimeout(() => {
                __DEV__ && console.log('[Print Window] Triggering print dialog...');
                try {
                  window.focus();
                  window.print();
                } catch (e) {
                  console.error('[Print Window] Print failed:', e);
                  alert('Không thể in tài liệu. Vui lòng thử lại.');
                }
              }, 300);
            }, 100);
          });
        });
      }).catch(err => {
        console.error('[Print Window] Asset loading failed:', err);
        // Still try to print
        setTimeout(() => {
          __DEV__ && console.log('[Print Window] Attempting print despite errors...');
          try { 
            window.print(); 
          } catch (e) {
            console.error('[Print Window] Print failed:', e);
          }
        }, 500);
      });
      
      // ✅ FIX: Reallocate heights, recalc pins, and arrange pages before print (final safety net)
      window.addEventListener('beforeprint', () => {
        __DEV__ && console.log('[Print Window] beforeprint event - reallocating heights and recalculating...');
        allocateHeightsPerPage();
        setTimeout(() => {
          recalcPins();
          arrangePages();
        }, 50);
      });
      
      // Auto-close after print
      window.addEventListener('afterprint', () => {
        __DEV__ && console.log('[Print Window] Print completed, closing window...');
        setTimeout(() => {
          try { 
            window.close(); 
          } catch (e) {
            __DEV__ && console.log('[Print Window] Could not close window (may require user action)');
          }
        }, 100);
      });
      
      // Fallback cleanup if user closes without printing
      setTimeout(() => {
        __DEV__ && console.log('[Print Window] Cleanup timeout reached (60s)');
        try { 
          window.close(); 
        } catch (e) {
          __DEV__ && console.log('[Print Window] Cleanup close failed');
        }
      }, 60000); // 1 minute timeout
      
    })();
  </script>
</body>
</html>`;

  // Replace document content
  printWindow.document.open();
  printWindow.document.write(fullHtml);
  printWindow.document.close();
}
